import { BackgroundVideo, VideoAspectRatio } from "../components/video"
import { Container, ContainerType } from "../components/layout"
import { Icons, iconOptionsWithPictograms } from "../../types"
import ImageEditor, { AspectRatio } from "../Image"
import RedesignButton, { RedesignButtonStyle } from "./Button"
import Typography, { TypographyElement } from "../RedesignTypography"
import { types, useAdminContext } from "react-bricks/frontend"

import { ConditionalWrapper } from "../components/util"
import React from "react"
import Stars from "../components/trust/stars"
import { useLocalesState } from "../../shared/context/locales"
import { useQueryTrustedShops } from "../../shared/hooks/use-query-trusted-shops"
import { useTranslation } from "react-i18next"

export interface HeroRedesignProps {
    headline: TypographyElement[]
    video: boolean
    cloudflareVideoId?: string
    cloudflareVideoIdMobile?: string
    scrim?: boolean
    showRating?: boolean
    showButton?: boolean
    buttonLabel: string
    buttonIcon?: Icons
    buttonHref?: string
    buttonTarget?: string
}

const TrustedShopsLogo = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36" className="h-5 w-5 shrink-0" aria-hidden>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.481 17.89c0 6.238 5.057 11.294 11.295 11.294 6.237 0 11.294-5.056 11.294-11.294S24.013 6.596 17.776 6.596c-6.238 0-11.295 5.056-11.295 11.294ZM0 17.947C0 8.047 8.025.024 17.924.024c9.9 0 17.924 8.024 17.924 17.923 0 9.9-8.025 17.925-17.924 17.925C8.024 35.872 0 27.847 0 17.947Zm20.192-6.69c.857 0 1.322.644 1.322 1.465 0 2.822-3.322 4.785-5.714 5.499.5-1.964 1.821-6.963 4.392-6.963Zm-3.963 14.784c3.07 0 5.356-2.215 6.999-4.642l-.894-.679c-1.178 1.428-2.784 3.357-4.785 3.357-1.535 0-2.284-1.287-2.284-2.678 0-.679.107-1.357.214-2.035l.498-.125c3.236-.806 8.928-2.223 8.928-6.052 0-2.25-2.499-3-4.356-3-5.535 0-9.462 5.213-9.462 10.533 0 3.285 1.892 5.32 5.142 5.32Z"
            fill="#fff"
        />
    </svg>
)

const HeroRedesign: types.Brick<HeroRedesignProps> = ({
    cloudflareVideoId,
    cloudflareVideoIdMobile,
    scrim,
    showRating,
    showButton,
    buttonLabel,
    buttonIcon,
    buttonHref,
    buttonTarget,
}) => {
    const { language, region } = useLocalesState()
    const { t } = useTranslation("translations")

    const { isAdmin } = useAdminContext()

    const trustedShopsEnabled = language === "de" && region === "DE"

    const { data: trustedShopsData } = useQueryTrustedShops()

    const rating: number = trustedShopsData?.grades?.["365days"]?.rating ?? 4.9
    const reviewCount: number = trustedShopsData?.grades?.overall?.count ?? 50000
    const ratingText = `${rating.toLocaleString(language)} | ${t("truststripe_count", {
        count: reviewCount,
        countString: reviewCount.toLocaleString(language),
    })}`

    return (
        <section
            className="relative h-screen-w/o-header w-full overflow-hidden"
            style={isAdmin ? { "--header-height": "0px" } : undefined}
        >
            <div className="absolute top-0 h-full w-full sm:hidden">
                <ImageEditor
                    aspectRatio={AspectRatio["9/19"]}
                    maxWidth={767}
                    className="absolute top-0 h-full w-full object-cover"
                    propName="imageMobile"
                />
                <BackgroundVideo id={cloudflareVideoIdMobile} aspectRatio={VideoAspectRatio["9/16"]} />
            </div>
            <div className="absolute top-0 hidden h-full w-full sm:block">
                <ImageEditor
                    aspectRatio={AspectRatio["16/9"]}
                    maxWidth={1920}
                    className="absolute top-0 h-full w-full object-cover"
                    propName="image"
                />
                <BackgroundVideo id={cloudflareVideoId} aspectRatio={VideoAspectRatio["16/9"]} />
            </div>
            {scrim && (
                <div
                    className="to-transparent absolute inset-0 bg-linear-to-r from-black/50 via-black/20"
                    aria-hidden
                />
            )}
            <div className="absolute inset-0 flex items-end pb-[100px] text-white xs:pb-[140px] sm:pb-[200px] md:items-center md:pb-0 short:items-start short:py-4">
                <Container type={ContainerType.Redesign} className="sm:grid sm:grid-cols-12 sm:gap-x-3.75 lg:gap-x-5">
                    <div className="col-span-8 md:col-span-6 xl:col-span-5">
                        <div className="short:mt-10 short:[&_span:last-child]:mb-3 short:[&_span]:text-3.5xl">
                            <Typography propName="headline" allowList={["S1", "S2", "S3"]} />
                        </div>
                        {showRating && (
                            <ConditionalWrapper
                                condition={trustedShopsEnabled}
                                wrapper={children => (
                                    <div className="flex">
                                        <a
                                            href="https://www.trustedshops.de/bewertung/info_X49E710FC236D0FD6604F8259314C8A7F.html"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {children}
                                        </a>
                                    </div>
                                )}
                            >
                                 <div className="mb-3.5 flex items-center gap-1.5 xs:mb-7 xs:mt-3 xs:gap-2 sm:mt-0 sm:gap-3 lg:mt-6">
                                    <Stars rating={rating} size="lg" color="#fff" emptyColor="#ffffff59" />
                                    <span className="whitespace-nowrap text-sm font-semibold underline sm:text-md">
                                        {ratingText}
                                    </span>
                                    {trustedShopsEnabled && <TrustedShopsLogo />}
                                </div>
                            </ConditionalWrapper>
                        )}
                        {showButton && (
                            <RedesignButton
                                title={buttonLabel}
                                href={buttonHref || "#"}
                                target={buttonTarget}
                                icon={buttonIcon}
                                style={RedesignButtonStyle.Primary}
                            />
                        )}
                    </div>
                </Container>
            </div>
        </section>
    )
}

HeroRedesign.schema = {
    name: "HeroRedesign",
    label: "Redesign Hero",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/HeroRedesign.png`,
    sideEditProps: [
        {
            name: "video",
            label: "Video",
            type: types.SideEditPropType.Boolean,
        },
        {
            name: "cloudflareVideoId",
            label: "Cloudflare Video ID 16x9",
            type: types.SideEditPropType.Text,
            show: props => props.video,
        },
        {
            name: "image",
            label: "Image Desktop",
            type: types.SideEditPropType.Image,
        },
        {
            name: "cloudflareVideoIdMobile",
            label: "Cloudflare Video ID (Mobile) 9x16",
            type: types.SideEditPropType.Text,
            show: props => props.video,
        },
        {
            name: "imageMobile",
            label: "Image Mobile",
            type: types.SideEditPropType.Image,
        },
        {
            name: "scrim",
            label: "Darken for readability",
            type: types.SideEditPropType.Boolean,
        },
        {
            name: "showRating",
            label: "Show rating",
            type: types.SideEditPropType.Boolean,
        },
        {
            name: "showButton",
            label: "Show button",
            type: types.SideEditPropType.Boolean,
        },
        {
            name: "buttonLabel",
            label: "Button label",
            type: types.SideEditPropType.Text,
            show: props => !!props.showButton,
        },
        {
            name: "buttonIcon",
            label: "Button icon (defaults to the offer icon)",
            type: types.SideEditPropType.Select,
            selectOptions: {
                display: types.OptionsDisplay.Select,
                options: iconOptionsWithPictograms,
            },
            show: props => !!props.showButton,
        },
        {
            name: "buttonHref",
            label: "Button link",
            type: types.SideEditPropType.Text,
            show: props => !!props.showButton,
        },
        {
            name: "buttonTarget",
            label: "Button target",
            type: types.SideEditPropType.Select,
            selectOptions: {
                display: types.OptionsDisplay.Select,
                options: [
                    { value: "_self", label: "Self" },
                    { value: "_blank", label: "Blank" },
                ],
            },
            show: props => !!props.showButton,
        },
    ],
    getDefaultProps: (): HeroRedesignProps => ({
        headline: [
            {
                type: "h1",
                children: [{ text: "Lorem ipsum dolor sit amet", S1: true }],
            },
        ],
        video: true,
        cloudflareVideoId: "",
        cloudflareVideoIdMobile: "",
        scrim: false,
        showRating: true,
        showButton: true,
        buttonLabel: "Lorem Ipsum",
        buttonIcon: Icons.cart,
        buttonHref: "#",
        buttonTarget: "_self",
    }),
}

export default HeroRedesign
